import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { DocumentState } from "@/services/backend/documents/types/state";
import { backendApi } from "@/services/backend/api";
import {
  NeedForAction,
  Summary,
} from "@/services/backend/htz/summary/types/summary";
import { VDI6022 } from "@/services/backend/htz/report/types/report";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { UUID } from "@bitory-gmbh/nidavellir";
import { updateCache } from "@/services/backend/_lib/update-cache";

/**
 * A save refetches the entry either way: on error to restore the server
 * state over the optimistic value, on success to pick up what the server
 * derived from it. See the create-auto-saving-input convention.
 */
const invalidatesSummary = (
  _result: unknown,
  _error: unknown,
  request: { id: UUID },
) =>
  [
    { type: "HtzSummary" as const, id: request.id },
    { type: "HtzSummary" as const, id: "LIST" },
  ] as const;

export const htzSummaryApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["HtzSummary"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzSummaryCreate: build.mutation<Summary, CreateRequest>({
        query: (request) => ({
          url: "/htz/summary",
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "HtzSummary", id: "LIST" }],
      }),
      htzSummaryShow: build.query<Summary, ShowRequest>({
        query: (request) => ({
          url: "/htz/summary",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzSummary"),
      }),
      htzSummaryList: build.query<Lst<Summary>, ListRequest>({
        query: (request) => ({
          url: "/htz/summary/list",
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) => providesList(result?.items, "HtzSummary"),
      }),
      htzSummaryPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
        ...revokeObjectURLOptions(),
        query: (request) => ({
          url: "/htz/summary",
          method: "GET",
          params: request,
          headers: {
            Accept: "application/pdf",
          },
          responseHandler: objectURLResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzSummaryPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        PDFRequest
      >({
        query: (request) => ({
          url: "/htz/summary",
          method: "GET",
          params: { ...request, download: true },
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzSummaryPatch: build.mutation<Summary, PatchRequest>({
        query: (request) => ({
          url: "/htz/summary",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesSummary,
      }),
      htzSummaryResetReferenceNumber: build.mutation<
        Summary,
        ResetReferenceNumberRequest
      >({
        query: (request) => ({
          url: `/htz/summary/reset-reference-number`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzSummary", id: request.id },
          { type: "HtzSummary", id: "LIST" },
        ],
      }),
      htzSummaryUpdateState: build.mutation<Summary, UpdateStateRequest>({
        query: (request) => ({
          url: "/htz/summary/state",
          method: "PATCH",
          body: request,
        }),
        // Festschreiben generates a document number and freezes the
        // summary, so the entry must be refetched either way.
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzSummary", id: request.id },
          { type: "HtzSummary", id: "LIST" },
        ],
      }),
      htzSummaryCreateItem: build.mutation<Summary, CreateItemRequest>({
        query: (request) => ({
          url: "/htz/summary/item",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzSummary", id: request.id },
          { type: "HtzSummary", id: "LIST" },
        ],
      }),
      htzSummaryPatchItem: build.mutation<Summary, PatchItemRequest>({
        query: (request) => ({
          url: "/htz/summary/item",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesSummary,
      }),
      htzSummaryRemoveItem: build.mutation<Summary, RemoveItemRequest>({
        query: (request) => ({
          url: "/htz/summary/item",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzSummary", id: request.id },
          { type: "HtzSummary", id: "LIST" },
        ],
      }),
      // Creating an item together with a new report, and removing an item
      // together with its report, change the item set and the reports of
      // the process — so both invalidate either way. The deleted report's
      // own entry is not invalidated: refetching it would only produce a 404
      // for the report page that is still mounted while navigating away.
      htzSummaryCreateItemWithNewReport: build.mutation<
        Summary,
        CreateItemWithNewReportRequest
      >({
        query: (request) => ({
          url: "/htz/summary/item/report",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzSummary", id: request.id },
          { type: "HtzSummary", id: "LIST" },
          { type: "HtzReport", id: "LIST" },
        ],
      }),
      htzSummaryRemoveItemAndDeleteReport: build.mutation<
        Summary,
        RemoveItemAndDeleteReportRequest
      >({
        query: (request) => ({
          url: "/htz/summary/item/report",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzSummary", id: request.id },
          { type: "HtzSummary", id: "LIST" },
          { type: "HtzReport", id: "LIST" },
        ],
      }),
      htzSummaryDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/htz/summary",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzSummary", id: request.id },
          { type: "HtzSummary", id: "LIST" },
        ],
      }),
    }),
  });

export const {
  useHtzSummaryCreateMutation,
  useHtzSummaryShowQuery,
  useHtzSummaryResetReferenceNumberMutation,
  useHtzSummaryListQuery,
  useLazyHtzSummaryPdfPreviewQuery,
  useHtzSummaryPdfDownloadMutation,
  useHtzSummaryPatchMutation,
  useHtzSummaryUpdateStateMutation,
  useHtzSummaryCreateItemMutation,
  useHtzSummaryPatchItemMutation,
  useHtzSummaryRemoveItemMutation,
  useHtzSummaryCreateItemWithNewReportMutation,
  useHtzSummaryRemoveItemAndDeleteReportMutation,
  useHtzSummaryDeleteMutation,
} = htzSummaryApi;

export interface CreateRequest {
  id: UUID;
  processId: UUID;
  companyId: UUID;
  branchId: UUID;
  customerId: UUID;
  serviceObjectId: UUID;
}

export interface ShowRequest {
  id: UUID;
}

export enum SummaryDocument {
  // The summary alone.
  SUMMARY = "summary",
  // The summary followed by the PDFs of all its reports.
  ALL = "all",
}

export interface PDFRequest {
  id: UUID;
  document: SummaryDocument;
  download?: boolean;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  report?: Filter<UUID>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

// PatchRequest updates the summary. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged (the backend
// `patch.Field` is "unset" when the JSON key is absent).
export interface PatchRequest {
  id: UUID;
  referenceNumber?: string;
  title?: string;
  subtitle?: string;
  date?: string | null;
  coverLetter?: string;
  postalAddress?: Address;
  serviceAddress?: Address;
  representative?: Representative;
}

// CreateItemRequest adds an item for a report. The item is prefilled
// from that report by the backend. An `order` below zero appends.
export interface CreateItemRequest {
  id: UUID;
  itemId: UUID;
  reportId: UUID;
  order: number;
}

// PatchItemRequest updates an item. All fields except `id` and `itemId`
// are optional; an omitted key leaves the value unchanged. Setting
// `order` moves the item and renumbers the remaining ones.
export interface PatchItemRequest {
  id: UUID;
  itemId: UUID;
  order?: number;
  airHandlingUnitName?: string;
  vdi6022Result?: VDI6022;
  needForAction?: NeedForAction;
  recommendations?: string;
}

export interface RemoveItemRequest {
  id: UUID;
  itemId: UUID;
}

// CreateItemWithNewReportRequest creates a new report for the summary's
// process, company, branch, customer and service object and adds an item
// summarizing it. Both ids are generated by the client. An `order` below
// zero appends.
export interface CreateItemWithNewReportRequest {
  id: UUID;
  itemId: UUID;
  reportId: UUID;
  order: number;
}

// RemoveItemAndDeleteReportRequest removes the item and deletes the report
// it summarizes. The backend refuses immutable reports and reports still
// referenced by another summary; nothing is changed then.
export interface RemoveItemAndDeleteReportRequest {
  id: UUID;
  itemId: UUID;
}

export interface UpdateStateRequest {
  id: UUID;
  state: DocumentState;
  stateSetBy: string;
}

export interface DeleteRequest {
  id: UUID;
}

/**
 * Patches the cached summary for an optimistic update. Defined here, at
 * module scope, so it keeps one identity for the whole app — see
 * {@link updateCache}.
 */
export const updateHtzSummaryCache = updateCache(
  htzSummaryApi,
  "htzSummaryShow",
);
